import type { MotionValue } from "motion/react";
import type { MouseEvent, PropsWithChildren } from "react";

import {
  SimpleResult,
  type IExtendableDto,
  type IProduceJoinDto,
  type IRideDto,
} from "../dto";
import type {
  DirectionEnum,
  EditFormMode,
  ImportAreaTypeEnum,
  RydeProcessTitle,
} from "../enums";
import { isSimpleDto } from "../helpers";
import type { IAreaWaypoint, TAreaWaypointDirection } from "./area.models";
import type { IGeoJSON, RequestBodyModel, RydeUiUnit } from "./common.models";
import type { ICoursesCalendarSlotDto } from "./courses-calendar.models";
import type { DateYMDString } from "./date-time.models";
import type { DrawerType } from "./drawers.models";
import type { IGeoAreaGeom } from "./geo-area.models";
import type {
  ICourseScheduleMetricItem,
  IProductionSummaryItem,
} from "./production";
import type { IRichViewLogsProps } from "./rich-models";

export type TCanResolve = (
  savedDto: IExtendableDto | undefined,
) => SimpleResult<void>;
export type TResolveFn = (
  value: SimpleResult<unknown> | PromiseLike<SimpleResult<unknown>>,
) => void;
export type TDrawerCloseFunction = (e?: MouseEvent) => void;
export type TDrawerOnCreateAction = (
  duplicateFromId: number | undefined,
  keepQuery?: boolean,
  initialData?: RequestBodyModel,
) => void;

export const simpleResultTrue = new SimpleResult<void>({ success: true });
export const simpleResultFalse = new SimpleResult<void>({ success: false });

export const canResolveAlways: TCanResolve = () => simpleResultTrue;

export const canResolveIfExists: TCanResolve = savedDto => {
  if (!savedDto || !isSimpleDto(savedDto)) {
    return simpleResultFalse;
  }

  return simpleResultTrue;
};

export const canResolveIfSimpleDtoExists: TCanResolve = savedDto => {
  if (!savedDto || !isSimpleDto(savedDto)) {
    return simpleResultFalse;
  }

  return simpleResultTrue;
};

export interface IInjectedDrawerProps extends PropsWithChildren {
  onCloseDrawer: TDrawerCloseFunction;
  drawerNumber?: number;
  customWidth?: string | MotionValue<string>;
}

export interface ICrudDrawerProps {
  id?: number;
  idCopyFrom?: number;
  mode?: EditFormMode;
}

export interface IDrawersCommonProps {
  isWithoutContainer?: boolean;
  onCloseCustomAction?: () => void;
  canResolve?: TCanResolve;
  resolveFn?: TResolveFn;
}

export interface IDrawerContainerProps<
  T extends DrawerType = DrawerType,
> extends IDrawersCommonProps {
  type: T;
}

export interface IAddNewBulkBookingsProps extends IDrawerContainerProps<DrawerType.AddNewBulkBookings> {
  passengerId?: number;
  date?: DateYMDString;
}

export interface IDrawerBookingsProductionProps extends IDrawerContainerProps<DrawerType.BookingsProduction> {
  bookingsIds: number[];
  direction?: DirectionEnum;
}

export interface IDrawerCoursesCalendarRideProduceProps extends IDrawerContainerProps<DrawerType.CoursesCalendarRideProduce> {
  /** Slots currently selected in the courses calendar (preview / bulk create). */
  slots: ICoursesCalendarSlotDto[];
  /** Required for legacy sequential create; optional when `shouldUseSharedProduceFlow` is true. */
  createSlotRide?: (slot: ICoursesCalendarSlotDto) => Promise<number>;
  /**
   * After slot table preview, open shared production preview
   * (polling, statistics, navigate to rides). Used for bulk and single-slot produce.
   */
  shouldUseSharedProduceFlow?: boolean;
  /** Called after rides were created or drawer closes successfully. */
  onAfterChange?: () => void;
}

export interface IEditFormDrawerProps
  extends IDrawerContainerProps<DrawerType.EditForm>, ICrudDrawerProps {
  entity: RydeUiUnit;
  subTitle?: string;
}

export interface IGenericExportDrawerProps extends IDrawerContainerProps<DrawerType.GenericExport> {
  entity: RydeUiUnit;
  selectedRowsData: IExtendableDto[];
  selectedIds?: number[];
}

export interface IGenericUploadDrawerProps extends IDrawerContainerProps<DrawerType.GenericUpload> {
  entity: RydeUiUnit;
}

export type IBulkPassengerNearBySearchDrawerProps =
  IDrawerContainerProps<DrawerType.BulkPassengerNearBySearch>;

export interface IBulkPassengersMessageProps extends IDrawerContainerProps<DrawerType.BulkPassengersMessage> {
  selectedPassengersIds: number[];
  /**
   * Recipients came from the passengers on the checked rides rather than from rows the user picked.
   * Two consequences: inactive passenger records are dropped from the list, count and send, and the
   * rides participation filter is hidden — the selected rides already scope who can receive this.
   */
  hasRecipientsDerivedFromRides?: boolean;
}

export interface IBulkActivateShiftTemplateProps extends IDrawerContainerProps<DrawerType.BulkActivateShiftTemplate> {
  selectedPassengersIds: number[];
}

export interface IBulkDriversMessageProps extends IDrawerContainerProps<DrawerType.BulkDriversMessage> {
  selectedDriverIds: number[];
}

export interface IRideEditFormDrawerProps
  extends IDrawerContainerProps<DrawerType.RideEditForm>, ICrudDrawerProps {
  duplicateReverseStations?: boolean;
}

export interface IBookingFormDrawerProps
  extends IDrawerContainerProps<DrawerType.BookingEditForm>, ICrudDrawerProps {}

export interface ICourseEditDrawerProps
  extends IDrawerContainerProps<DrawerType.CourseEditForm>, ICrudDrawerProps {
  /**
   * The drawer was opened on a course draft the caller already filled in (bulk "create course from stations").
   * Nothing is dirty yet from the form's point of view, so Save must be enabled from the first render.
   */
  hasPrefilledDraft?: boolean;
}

export interface IJourneyEditDrawerProps
  extends IDrawerContainerProps<DrawerType.JourneyEditForm>, ICrudDrawerProps {
  subTitle?: string;
}

export interface IJourneyDuplicateProps extends IDrawerContainerProps<DrawerType.JourneyDuplicate> {
  assignRides?: IRideDto[];
  dto?: IExtendableDto;
}

export interface IStationRouteEditDrawerProps
  extends
    IDrawerContainerProps<DrawerType.StationRouteEditForm>,
    ICrudDrawerProps {
  rideOpenerId?: number;
}

export interface INotificationEditDrawerProps
  extends
    IDrawerContainerProps<DrawerType.NotificationEditForm>,
    ICrudDrawerProps {}

export interface IDrawerNewOrDuplicateProps
  extends IDrawerContainerProps<DrawerType.NewOrDuplicate>, ICrudDrawerProps {
  entity: RydeUiUnit;
  onCreateCustomAction?: TDrawerOnCreateAction;
  /** What a new record starts with when "New" is chosen; a duplicate starts from its source. */
  newItemDraft?: Partial<IExtendableDto>;
}

export interface IHistoryLogDrawerProps extends IDrawerContainerProps<DrawerType.HistoryLog> {
  id: number;
  entity: RydeUiUnit;
}

export type IDrawerCustomProductionProps =
  IDrawerContainerProps<DrawerType.CustomProduction>;

export type IBulkCreateBookingsProps =
  IDrawerContainerProps<DrawerType.BulkCreateBookings>;

export type IBulkUploadBookingsProps =
  IDrawerContainerProps<DrawerType.BulkUploadBookings>;

export type IBulkUpdateBookingsDateTimeProps =
  IDrawerContainerProps<DrawerType.BulkUpdateBookingsDateTime>;

export interface INotificationsDrawerProps extends IDrawerContainerProps<DrawerType.Notifications> {
  id?: number;
}

export interface IProductionViewLogsDrawerProps
  extends
    IDrawerContainerProps<DrawerType.ProductionViewLogs>,
    IRichViewLogsProps {}

export interface IEtlViewLogsDrawerProps
  extends IDrawerContainerProps<DrawerType.EtlViewLogs>, IRichViewLogsProps {}

export interface IPriceRateSearchLogsDrawerProps extends IDrawerContainerProps<DrawerType.PriceRateSearchLogs> {
  rideId: number;
  supplierId?: number | null;
  vehicleTypeId?: number | null;
  priceRateId?: number | null;
}

export type IUpdateBasePriceProps =
  IDrawerContainerProps<DrawerType.UpdateBasePrice>;

export interface IProductionPreviewProps
  extends IDrawerContainerProps<DrawerType.ProductionPreview>, IProduceJoinDto {
  summaryItems?: IProductionSummaryItem[];
  isCloseAfterProductionFinish?: boolean;
  isRedirectToProducedRidesAfterProductionFinish?: boolean;
}

export interface IImportAreasProps extends IDrawerContainerProps<DrawerType.ImportAreas> {
  importType: ImportAreaTypeEnum;
  title: string;
  onSelectPolygons?: (geoAreasGeom: IGeoAreaGeom[]) => void;
}

export interface IImportBusStationsProps extends IDrawerContainerProps<DrawerType.ImportBusStations> {
  title: string;
}

export interface IProcessDrawerProps extends IDrawerContainerProps<DrawerType.ProcessDrawer> {
  processId: number;
  entity: RydeUiUnit;
  processTitle: RydeProcessTitle;
  currentEntityPage?: RydeUiUnit;
  avoidResetSelectedIds?: boolean;
  // company the process belongs to, for global pages not scoped to the selected company
  companyId?: number;
}

export interface IRideHistoryLogDrawerProps extends IDrawerContainerProps<DrawerType.RideHistoryLog> {
  id: number;
  entity: RydeUiUnit;
}

export type IAdministrativeModeDrawerProps =
  IDrawerContainerProps<DrawerType.AdministrativeMode>;

export type ISupplierPriceRatesPasswordDrawerProps =
  IDrawerContainerProps<DrawerType.SupplierPriceRatesPassword>;

export type ICopyToSupplierPriceRatesProps =
  IDrawerContainerProps<DrawerType.CopyToSupplierPriceRates>;

export type IBackupCreateDrawerProps =
  IDrawerContainerProps<DrawerType.BackupCreate>;

export type IApiKeyCreateDrawerProps =
  IDrawerContainerProps<DrawerType.ApiKeyCreate>;

export interface IAiConversationDetailsDrawerProps extends IDrawerContainerProps<DrawerType.AiConversationDetails> {
  rawReasoning: string;
}

export type IUserActionsHistoryDrawerProps =
  IDrawerContainerProps<DrawerType.UserActionsHistory>;

export interface IHelpTopicDrawerProps extends IDrawerContainerProps<DrawerType.HelpTopic> {
  helpTopikUrl: string;
  topicTitle?: string;
}

export type IHubStationsInfoDrawerProps =
  IDrawerContainerProps<DrawerType.HubStationsInfo>;

export interface ICourseSchedulesPreviewDrawerProps extends IDrawerContainerProps<DrawerType.CourseSchedulesPreview> {
  schedules: ICourseScheduleMetricItem[];
  title?: string;
}

export interface IRelatedEntitiesDrawerProps extends IDrawerContainerProps<DrawerType.RelatedEntities> {
  entity: RydeUiUnit;
  entityId: number;
  entityLabel?: string;
}

export interface IEditAreaWaypointsDrawerProps extends IDrawerContainerProps<DrawerType.EditAreaWaypoints> {
  direction: TAreaWaypointDirection;
  waypoints: IAreaWaypoint[];
  existingWaypoints?: IAreaWaypoint[];
  polygon?: IGeoJSON;
  areaName?: string;
  disabled?: boolean;
}
